import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { CheckCircle2, Clock3, QrCode, ShieldCheck, Smartphone, Upload, Camera, AlertTriangle, CheckCircle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Unable to read file'));
    reader.readAsDataURL(file);
  });
}

function StepCard({ step, stepIndex, totalSteps, capture, onPick, onUpload, uploading, uploaded, disabled }) {
  const fileRef = React.useRef(null);
  const openCamera = () => fileRef.current?.click();
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 text-sm font-semibold">
              {uploaded ? <CheckCircle size={14} /> : stepIndex}
            </span>
            <div>
              <p className="text-[10px] uppercase tracking-[0.24em] text-slate-400">Step {stepIndex} of {totalSteps}</p>
              <p className="text-xs text-slate-500">{step.key === 'selfie' ? 'Front camera' : 'Document scan'}</p>
            </div>
          </div>
          <p className="text-sm font-semibold text-slate-900">{step.name}</p>
          <p className="text-xs text-slate-500 mt-0.5">{step.help}</p>
        </div>
        {uploaded ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
            <CheckCircle2 size={12} /> Uploaded
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
            <Clock3 size={12} /> Pending
          </span>
        )}
      </div>

      {capture?.preview ? (
        <div className="mb-3 overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
          <img src={capture.preview} alt={step.name} className="h-48 w-full object-cover" />
        </div>
      ) : (
        <div className="mb-3 flex h-48 items-center justify-center rounded-xl border border-dashed border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 text-slate-400">
          <div className="text-center max-w-xs px-4">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm">
              <Camera size={22} />
            </div>
            <p className="text-sm font-medium text-slate-600">No capture selected yet</p>
            <p className="text-xs text-slate-400 mt-1">Open the camera to scan this step and preview it before uploading.</p>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2 sm:flex-row">
        <Button
          type="button"
          className="flex-1 bg-emerald-700 text-white hover:bg-emerald-800"
          onClick={openCamera}
          disabled={disabled}
        >
          <Camera size={14} className="mr-2" />
          {capture?.file ? 'Retake with camera' : 'Open camera'}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="flex-1 border-slate-200"
          disabled={disabled || !capture?.file || uploading}
          onClick={onUpload}
        >
          <Upload size={14} className="mr-2" />
          {uploading ? 'Uploading...' : 'Upload capture'}
        </Button>
        <Input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture={step.capture}
          className="hidden"
          onChange={onPick}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

export default function KycCapture() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [captures, setCaptures] = useState({});
  const [uploadingStep, setUploadingStep] = useState('');
  const [completing, setCompleting] = useState(false);

  const loadSession = async () => {
    try {
      const { data } = await api.get(`/verification/capture-session/${token}`);
      setSession(data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Capture session could not be loaded.');
      setSession(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    loadSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!session || ['completed', 'expired'].includes(session.status)) return;
    const timer = setInterval(loadSession, 4000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.status, token]);

  const requiredSteps = useMemo(() => session?.required_documents || [], [session]);
  const uploadedSteps = useMemo(() => new Set((session?.documents || []).map(doc => String(doc.capture_step || '').toLowerCase())), [session]);
  const completed = session?.status === 'completed';
  const expired = session?.status === 'expired' || session?.expired;

  const handlePick = async (stepKey, file) => {
    if (!file) return;
    const dataUrl = await readFileAsDataUrl(file);
    setCaptures(prev => ({
      ...prev,
      [stepKey]: {
        file,
        file_name: file.name,
        file_type: file.type || 'image/jpeg',
        preview: dataUrl,
        data: dataUrl.split(',')[1],
      },
    }));
  };

  const handleUpload = async (stepKey) => {
    const capture = captures[stepKey];
    if (!capture) return;
    setUploadingStep(stepKey);
    try {
      await api.post(`/verification/capture-session/${token}/document`, {
        step: stepKey,
        file_name: capture.file_name,
        file_type: capture.file_type,
        file_data: capture.data,
      });
      toast.success('Capture uploaded');
      setCaptures(prev => {
        const next = { ...prev };
        delete next[stepKey];
        return next;
      });
      await loadSession();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingStep('');
    }
  };

  const handleComplete = async () => {
    setCompleting(true);
    try {
      const { data } = await api.post(`/verification/capture-session/${token}/complete`, {});
      toast.success(data.message || 'Verification submitted');
      await loadSession();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to complete verification');
    } finally {
      setCompleting(false);
    }
  };

  const allRequiredUploaded = requiredSteps.every(step => uploadedSteps.has(step.key));
  const completedSteps = requiredSteps.filter(step => uploadedSteps.has(step.key)).length;
  const progressPercent = requiredSteps.length ? Math.round((completedSteps / requiredSteps.length) * 100) : 0;
  const nextStep = requiredSteps.find(step => !uploadedSteps.has(step.key)) || requiredSteps[requiredSteps.length - 1];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-emerald-700 border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg border border-slate-200 shadow-sm rounded-3xl">
          <CardContent className="p-6 text-center">
            <AlertTriangle size={40} className="mx-auto mb-3 text-amber-600" />
            <h1 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Capture session unavailable</h1>
            <p className="mt-2 text-sm text-slate-600">{error}</p>
            <div className="mt-5 flex flex-col gap-2">
              <Button className="bg-emerald-700 text-white hover:bg-emerald-800" onClick={() => navigate('/dashboard/verification')}>
                Go to Verification
              </Button>
              <Button variant="outline" onClick={loadSession}>Try Again</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-xl border border-slate-200 shadow-sm rounded-3xl">
          <CardContent className="p-6 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
              <CheckCircle2 size={30} />
            </div>
            <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Verification submitted</h1>
            <p className="mt-2 text-sm text-slate-600">Your live capture has been sent to Jjuma Pay for review.</p>
            <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
              <Button className="bg-emerald-700 text-white hover:bg-emerald-800" onClick={() => navigate('/dashboard/verification')}>
                Return to dashboard
              </Button>
              <Button variant="outline" onClick={loadSession}>
                Refresh status
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (expired) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg border border-slate-200 shadow-sm rounded-3xl">
          <CardContent className="p-6 text-center">
            <QrCode size={40} className="mx-auto mb-3 text-slate-500" />
            <h1 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Capture session expired</h1>
            <p className="mt-2 text-sm text-slate-600">Please go back to your dashboard and create a new QR capture session.</p>
            <div className="mt-5">
              <Button className="bg-emerald-700 text-white hover:bg-emerald-800" onClick={() => navigate('/dashboard/verification')}>
                Open Verification
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
        <Card className="border border-slate-200 shadow-sm rounded-3xl overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-700 to-emerald-800 px-6 py-6 text-white">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15">
                <Smartphone size={20} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-emerald-100/80">Jjuma Pay</p>
                <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Mobile KYC Capture</h1>
              </div>
            </div>
            <p className="mt-4 text-sm text-emerald-50/90">
              Use your phone camera to capture your verification documents and selfie. Your desktop dashboard will update automatically.
            </p>
          </div>

          <CardContent className="p-6 space-y-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Document Type</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">{session?.document_type === 'passport' ? 'Passport' : 'National ID'}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Status</p>
                <p className="mt-1 text-sm font-semibold text-slate-900 capitalize">{session?.status}</p>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.24em] text-slate-400">Capture progress</p>
                  <p className="text-sm font-semibold text-slate-900">{completedSteps}/{requiredSteps.length} steps completed</p>
                </div>
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
                  <ArrowRight size={12} /> {nextStep?.name || 'All done'}
                </span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-emerald-600 to-emerald-500 transition-all"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <div className="mt-4 grid gap-2 sm:grid-cols-3">
                {requiredSteps.map((step, index) => {
                  const done = uploadedSteps.has(step.key);
                  const current = !done && step.key === nextStep?.key;
                  return (
                    <div
                      key={step.key}
                      className={`flex items-center gap-3 rounded-2xl border px-3 py-3 ${done ? 'border-emerald-200 bg-emerald-50' : current ? 'border-emerald-300 bg-emerald-50/70' : 'border-slate-200 bg-slate-50'}`}
                    >
                      <span className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold ${done ? 'bg-emerald-700 text-white' : current ? 'bg-emerald-100 text-emerald-700' : 'bg-white text-slate-400'}`}>
                        {done ? <CheckCircle2 size={14} /> : index + 1}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{step.name}</p>
                        <p className="text-xs text-slate-500">{done ? 'Captured' : current ? 'Next to capture' : 'Waiting'}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-sm text-emerald-900">
              <div className="flex items-start gap-3">
                <ShieldCheck size={18} className="mt-0.5 flex-shrink-0" />
                <div className="space-y-1">
                  <p className="font-semibold">Before you start</p>
                  <ul className="list-disc pl-4 space-y-1 text-emerald-900/90">
                    <li>Make sure the document is clear and well lit.</li>
                    <li>You can retake any image before uploading.</li>
                    <li>Selfie capture uses your phone camera, not gallery uploads.</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {requiredSteps.map((step, index) => (
                <StepCard
                  key={step.key}
                  stepIndex={index + 1}
                  totalSteps={requiredSteps.length}
                  step={{
                    ...step,
                    help: step.key === 'selfie'
                      ? 'Take a live selfie using the front camera.'
                      : 'Capture this document with your phone camera.',
                  }}
                  capture={captures[step.key]}
                  uploaded={uploadedSteps.has(step.key)}
                  uploading={uploadingStep === step.key}
                  disabled={uploadingStep && uploadingStep !== step.key}
                  onPick={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 5 * 1024 * 1024) {
                      toast.error('File too large. Max 5MB');
                      return;
                    }
                    await handlePick(step.key, file);
                    e.target.value = '';
                  }}
                  onUpload={() => handleUpload(step.key)}
                />
              ))}
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-slate-500">
                {allRequiredUploaded
                  ? 'All required documents are uploaded. Tap submit to finish verification.'
                  : 'Upload each required step to continue.'}
              </p>
              <Button
                className="bg-emerald-700 text-white hover:bg-emerald-800"
                disabled={!allRequiredUploaded || completing}
                onClick={handleComplete}
              >
                {completing ? 'Submitting...' : 'Submit Verification'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
